import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Filter20Regular,
    ArrowSort20Regular,
    Grid20Regular,
    EyeOff20Regular,
    ChevronDown12Regular,
    ChevronDown20Regular,
    ArrowExport20Regular,
    Table20Regular,
    ArrowDownload20Regular,
    MoreVertical16Regular,
    Comment16Filled,
    CommentAdd16Regular,
    Wand16Regular,
    Wand20Regular,
    Dismiss20Regular,
    ArrowClockwise16Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider } from '@fluentui/react-components';
import { OverlayDrawer, DrawerBody } from '@fluentui/react-components';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { Dropdown } from 'react-bootstrap';
import {
    GetLessons,
    PostLesson,
    PatchLesson,
    DeleteLesson,
    ExportLessonsCSV,
    DownloadLessonsTemplate,
    UploadLessonsCSV,
    BulkUpdateLessons,
    BulkDeleteLessons,
    GetProjectLessonTags,
    GetLessonsAISummary,
    clearLessonsSummary,
} from '../../redux/lessonSlice';
import styles from '../TaskManagement/css/index.module.css';
import { CustomAvatar } from '../TaskManagement';
import ResizableColumnHeader from '../TaskManagement/ResizableColumnHeader';
import { formatDistanceToNow, format, isAfter, subWeeks } from 'date-fns';
import { getGroupBySettings, saveGroupBySettings } from '../../utils/groupByPersistence';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import LessonTagManagementMenu from './LessonTagManagementMenu';
import LessonDetailsSidebar from './LessonDetailsSidebar';

const LESSONS_AI_SUMMARY_SECTIONS = [
    { id: 'key-themes', label: 'Key Themes & Patterns', key: 'key_themes_html' },
    { id: 'most-significant-lessons', label: 'Most Significant Lessons', key: 'most_significant_lessons_html' },
    { id: 'process-improvements', label: 'Process Improvements', key: 'process_improvements_html' },
    { id: 'stakeholder-insights', label: 'Stakeholder-Specific Insights', key: 'stakeholder_insights_html' },
    { id: 'effectiveness-of-actions', label: 'Effectiveness of Actions', key: 'effectiveness_of_actions_html' },
    { id: 'recurring-risk-areas', label: 'Recurring Risk Areas', key: 'recurring_risk_areas_html' },
    { id: 'strategic-recommendations', label: 'Strategic Recommendations', key: 'strategic_recommendations_html' },
];

const LESSONS_AI_SHOW_ALL = 'show-all';

const Lessons = () => {
    const dispatch = useDispatch();
    const { lessons, buttonLoading, exportCSVLoading, downloadTemplateLoading, uploadCSVBtnLoading, lessonTags, lessonsSummaryData, lessonsSummaryLoading, lessonsSummaryError } = useSelector(
        state =>
            state.lesson || {
                lessons: [],
                buttonLoading: false,
                exportCSVLoading: false,
                downloadTemplateLoading: false,
                uploadCSVBtnLoading: false,
                lessonTags: [],
                lessonsSummaryData: null,
                lessonsSummaryLoading: false,
                lessonsSummaryError: null,
            },
    );
    const { selectedProject } = useSelector(state => state.projectData);

    // Dynamic audience options based on investor_name
    const AUDIENCE_OPTIONS = React.useMemo(
        () => [
            { value: 'BEYOND', label: 'Beyond', color: '#78be21' },
            { value: 'INVESTOR', label: selectedProject?.investor_name || 'Investor', color: '#4ADEDE' },
            { value: 'SHARED', label: 'Shared', color: '#FFB454' },
        ],
        [selectedProject?.investor_name],
    );

    // Helper function to convert audience value for backend
    // If it's 'INVESTOR' and investor_name exists, send investor_name instead
    const getAudienceValueForBackend = audienceValue => {
        if (audienceValue === 'INVESTOR' && selectedProject?.investor_name) {
            return selectedProject.investor_name;
        }
        return audienceValue;
    };

    const [newIssue, setNewIssue] = React.useState('');
    const [newAction, setNewAction] = React.useState('');
    const [newRecomendation, setNewRecomendation] = React.useState('');
    const [newTag, setNewTag] = React.useState('');
    const [newAudience, setNewAudience] = React.useState('');

    const [editingLessonId, setEditingLessonId] = React.useState(null);
    const [editingFieldValues, setEditingFieldValues] = React.useState({});
    const [editingFieldName, setEditingFieldName] = React.useState(null);

    // Filters / sort state (simplified version of RAID)
    const [filters, setFilters] = React.useState([{ column: '', value: '', id: Date.now() }]);
    const [sortColumn, setSortColumn] = React.useState('');
    const [sortOrder, setSortOrder] = React.useState('');

    // Group-by state (simplified from RAID)
    const [groupByColumn, setGroupByColumn] = React.useState('');
    const [groupBySort, setGroupBySort] = React.useState('');
    const [collapsedGroups, setCollapsedGroups] = React.useState(new Set());

    const [selectedRows, setSelectedRows] = React.useState([]);
    const [hoveredRowId, setHoveredRowId] = React.useState(null);
    const [headerIdHovered, setHeaderIdHovered] = React.useState(false);

    // Column visibility (simpler version of RAID)
    const [showAllColumns, setShowAllColumns] = React.useState(true);
    const [hiddenColumns, setHiddenColumns] = React.useState([]);

    const [selectedRowForDelete, setSelectedRowForDelete] = React.useState(null);
    const deleteModalRef = React.useRef(null);
    const fileInputRef = React.useRef(null);
    const tableContainerRef = React.useRef(null);
    const addRowRef = React.useRef(null);
    const newIssueInputRef = React.useRef(null);
    const [highlightAddRow, setHighlightAddRow] = React.useState(false);

    const [lessonSidebarOpen, setLessonSidebarOpen] = React.useState(false);
    const [selectedLessonForSidebar, setSelectedLessonForSidebar] = React.useState(null);
    const [showLensAIAnalysis, setShowLensAIAnalysis] = React.useState(false);
    const [selectedSummaryTab, setSelectedSummaryTab] = React.useState('key-themes');

    // Column widths and drag order (mirrors RAID but simplified for Lessons)
    const [columnWidths, setColumnWidths] = React.useState({
        issue: 260,
        action: 260,
        recomendation: 260,
        tag: 160,
        audience: 120,
        loggedOn: 200,
        updatedOn: 200,
    });

    const handleColumnResize = (columnKey, newWidth) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnKey]: newWidth,
        }));
    };

    const defaultColumnOrder = ['issue', 'action', 'recomendation', 'tag', 'audience', 'loggedOn', 'updatedOn'];
    const [columnOrder, setColumnOrder] = React.useState(defaultColumnOrder);

    const isColumnVisible = column => {
        if (showAllColumns) return true;
        return !hiddenColumns.includes(column);
    };

    const handleShowAllColumns = checked => {
        if (checked) {
            setShowAllColumns(true);
            setHiddenColumns([]);
        } else {
            setShowAllColumns(false);
        }
    };

    const handleColumnVisibility = (column, checked) => {
        if (column === 'showAll') {
            handleShowAllColumns(checked);
            return;
        }

        if (checked) {
            // Show column
            setHiddenColumns(prev => prev.filter(col => col !== column));
            if (hiddenColumns.length === 0) {
                setShowAllColumns(true);
            }
        } else {
            // Hide column
            setHiddenColumns(prev => [...prev, column]);
            setShowAllColumns(false);
        }
    };

    const getColumnVisibilityOptions = () => [
        { value: 'showAll', label: 'Show all' },
        { value: 'issue', label: 'Issue / Observation' },
        { value: 'action', label: 'Action Taken (This Project)' },
        { value: 'recomendation', label: 'Recommendation (Future)' },
        { value: 'tag', label: 'Tag' },
        { value: 'audience', label: 'Audience' },
        { value: 'loggedOn', label: 'Logged on' },
        { value: 'updatedOn', label: 'Updated on' },
    ];

    const SortableHeaderCell = ({ id, width, children }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
        const style = {
            transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
            transition,
            opacity: isDragging ? 0.6 : 1,
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
            '--column-width': `${width}px`,
            cursor: isDragging ? 's-resize' : undefined,
        };
        return (
            <th ref={setNodeRef} style={style} className={`${styles.headerRowItem} ${isDragging ? 'isDragging' : ''}`} data-sortable-id={id} {...attributes} {...listeners}>
                <div style={{ width: '100%' }}>{children}</div>
            </th>
        );
    };

    React.useEffect(() => {
        if (selectedProject?.id) {
            dispatch(GetLessons());
            dispatch(GetProjectLessonTags());
        }
    }, [dispatch, selectedProject?.id]);

    // Load persisted group-by settings when project changes (like RAID)
    React.useEffect(() => {
        if (selectedProject?.id) {
            const saved = getGroupBySettings(selectedProject.id + '_lessons');
            if (saved) {
                setGroupByColumn(saved.column || '');
                setGroupBySort(saved.sort || '');
                setCollapsedGroups(new Set(saved.collapsedGroups || []));
            } else {
                setGroupByColumn('');
                setGroupBySort('');
                setCollapsedGroups(new Set());
            }
        }
    }, [selectedProject?.id]);

    // Persist group-by settings when they change
    React.useEffect(() => {
        if (!selectedProject?.id) return;
        saveGroupBySettings(selectedProject.id + '_lessons', {
            column: groupByColumn,
            sort: groupBySort,
            collapsedGroups: Array.from(collapsedGroups),
        });
    }, [selectedProject?.id, groupByColumn, groupBySort, collapsedGroups]);

    // Auto-load latest AI summary when the Lens AI drawer opens (mirrors IRL behavior)
    React.useEffect(() => {
        if (!showLensAIAnalysis) return;
        if (!selectedRows || selectedRows.length === 0) return;
        if (lessonsSummaryLoading || lessonsSummaryData || lessonsSummaryError) return;
        dispatch(GetLessonsAISummary({ lessonIds: selectedRows, regenerate: false }));
    }, [showLensAIAnalysis, selectedRows, lessonsSummaryLoading, lessonsSummaryData, lessonsSummaryError, dispatch]);

    const formatDate = dateString => {
        if (!dateString) return '-- --';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch (e) {
            return '-- --';
        }
    };

    const formatLastUpdated = timestamp => {
        if (!timestamp) return '--';
        const date = new Date(timestamp);
        const oneWeekAgo = subWeeks(new Date(), 1);

        if (isAfter(date, oneWeekAgo)) {
            return formatDistanceToNow(date, { addSuffix: true });
        }
        return format(date, 'MMM d, yyyy');
    };

    const handleCreateLesson = async () => {
        const value = newIssue.trim();
        if (!value) return;
        await dispatch(
            PostLesson({
                issue: value,
                action: newAction.trim() || null,
                recomendation: newRecomendation.trim() || null,
                tag: newTag.trim() || null,
                audience: newAudience ? getAudienceValueForBackend(newAudience) : null,
            }),
        );
        setNewIssue('');
        setNewAction('');
        setNewRecomendation('');
        setNewTag('');
        setNewAudience('');
    };

    const handleExportLessonsCSV = () => {
        if (!exportCSVLoading) {
            dispatch(ExportLessonsCSV());
        }
    };

    const handleExportSelectedLessonsCSV = () => {
        if (exportCSVLoading || selectedRows.length === 0) {
            return;
        }
        dispatch(ExportLessonsCSV(selectedRows));
    };

    const startEditing = (e, lesson, field) => {
        e.stopPropagation();
        setEditingLessonId(lesson.id);
        setEditingFieldName(field);
        setEditingFieldValues({
            issue: lesson.issue || '',
            action: lesson.action || '',
            recomendation: lesson.recomendation || '',
            tag: lesson.tag || '',
            audience: lesson.audience || '',
        });
    };

    const saveEditing = async lesson => {
        if (!editingLessonId) return;
        const payload = {};
        if (editingFieldValues.issue.trim() !== (lesson.issue || '')) payload.issue = editingFieldValues.issue.trim();
        if ((editingFieldValues.action || '').trim() !== (lesson.action || '')) payload.action = editingFieldValues.action.trim() || null;
        if ((editingFieldValues.recomendation || '').trim() !== (lesson.recomendation || '')) payload.recomendation = editingFieldValues.recomendation.trim() || null;
        if ((editingFieldValues.tag || '').trim() !== (lesson.tag || '')) payload.tag = editingFieldValues.tag.trim() || null;
        if ((editingFieldValues.audience || '') !== (lesson.audience || '')) {
            payload.audience = editingFieldValues.audience ? getAudienceValueForBackend(editingFieldValues.audience) : null;
        }

        if (Object.keys(payload).length > 0) {
            await dispatch(
                PatchLesson({
                    id: lesson.id,
                    body: payload,
                }),
            );
        }

        setEditingLessonId(null);
        setEditingFieldName(null);
        setEditingFieldValues({});
    };

    const cancelEditing = () => {
        setEditingLessonId(null);
        setEditingFieldName(null);
        setEditingFieldValues({});
    };

    const handleDeleteRow = row => {
        setSelectedRowForDelete(row);
        deleteModalRef?.current?.click();
    };

    const handleLessonsFileUpload = event => {
        const file = event.target.files[0];
        if (file && !uploadCSVBtnLoading) {
            const formData = new FormData();
            formData.append('file', file);
            dispatch(UploadLessonsCSV(formData));
        }
        event.target.value = '';
    };

    const handleOpenLessonSidebar = row => {
        setSelectedLessonForSidebar(row);
        setLessonSidebarOpen(true);
    };

    const handleCloseLessonSidebar = () => {
        setLessonSidebarOpen(false);
        setSelectedLessonForSidebar(null);
    };

    // Filter / sort helpers
    const handleAddFilter = () => {
        setFilters(prev => [...prev, { column: '', value: '', id: Date.now() }]);
    };

    const handleRemoveFilter = filterId => {
        setFilters(prev => prev.filter(filter => filter.id !== filterId));
    };

    const handleUpdateFilter = (filterId, field, value) => {
        setFilters(prev => prev.map(filter => (filter.id === filterId ? { ...filter, [field]: value } : filter)));
    };

    const handleClearAllFilters = () => {
        setFilters([{ column: '', value: '', id: Date.now() }]);
    };

    const handleClearSort = () => {
        setSortColumn('');
        setSortOrder('');
    };

    const isRowSelected = id => selectedRows.includes(id);

    const toggleRowSelected = id => {
        setSelectedRows(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const handleAddLessonClick = () => {
        // Scroll to the add row and focus the Issue input, then briefly highlight the row
        requestAnimationFrame(() => {
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
            }
            if (addRowRef.current) {
                addRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
            if (newIssueInputRef.current) {
                newIssueInputRef.current.focus();
            }
            setHighlightAddRow(true);
            setTimeout(() => setHighlightAddRow(false), 1000);
        });
    };

    const getFilterColumnOptions = () => [
        { value: 'tag', label: 'Tag' },
        { value: 'audience', label: 'Audience' },
    ];

    const getSortColumnOptions = () => [
        { value: 'issue', label: 'Issue / Observation' },
        { value: 'tag', label: 'Tag' },
        { value: 'audience', label: 'Audience' },
        { value: 'loggedOn', label: 'Logged on' },
        { value: 'updatedOn', label: 'Updated on' },
    ];

    const getSortOrderOptions = () => [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' },
    ];

    const getAudienceInfo = value => {
        // If value matches investor_name, treat it as INVESTOR
        if (value === selectedProject?.investor_name) {
            return AUDIENCE_OPTIONS.find(o => o.value === 'INVESTOR') || { value: null, label: 'Unspecified', color: 'var(--Neutral-Gray-6)' };
        }
        const opt = AUDIENCE_OPTIONS.find(o => o.value === value);
        if (opt) return opt;
        return { value: null, label: 'Unspecified', color: 'var(--Neutral-Gray-6)' };
    };

    const getFilterValueOptions = column => {
        const items = lessons || [];
        if (!column) return [];

        const addUnique = (acc, value, label = null) => {
            if (!value) return acc;
            if (!acc.find(opt => opt.value === value)) {
                acc.push({ value, label: label || value });
            }
            return acc;
        };

        switch (column) {
            case 'tag':
                return items.reduce((acc, item) => addUnique(acc, item.tag, item.tag), []);
            case 'audience':
                return AUDIENCE_OPTIONS.reduce((acc, opt) => addUnique(acc, opt.value, opt.label), []);
            default:
                return [];
        }
    };

    const applyFilters = items => {
        if (!items || !filters?.length) return items;

        return items.filter(item =>
            filters.every(filter => {
                if (!filter.column || !filter.value) return true;

                switch (filter.column) {
                    case 'tag':
                        return item.tag === filter.value;
                    case 'audience':
                        // Handle both 'INVESTOR' and investor_name from backend
                        if (filter.value === 'INVESTOR') {
                            return item.audience === 'INVESTOR' || item.audience === selectedProject?.investor_name;
                        }
                        return item.audience === filter.value;
                    default:
                        return true;
                }
            }),
        );
    };

    const applySorting = items => {
        if (!items || !sortColumn || !sortOrder) return items;

        const sorted = [...items].sort((a, b) => {
            const getComparable = row => {
                switch (sortColumn) {
                    case 'issue':
                        return (row.issue || '').toLowerCase();
                    case 'tag':
                        return (row.tag || '').toLowerCase();
                    case 'audience': {
                        // Handle both 'INVESTOR' and investor_name from backend
                        const audienceValue = row.audience === selectedProject?.investor_name ? 'INVESTOR' : row.audience;
                        const opt = AUDIENCE_OPTIONS.find(o => o.value === audienceValue);
                        return (opt?.label || '').toLowerCase();
                    }
                    case 'loggedOn':
                        return row.created ? new Date(row.created).getTime() : 0;
                    case 'updatedOn':
                        return row.last_updated ? new Date(row.last_updated).getTime() : 0;
                    default:
                        return '';
                }
            };

            const aVal = getComparable(a);
            const bVal = getComparable(b);

            if (aVal === bVal) return 0;

            const cmp = typeof aVal === 'number' && typeof bVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));

            return sortOrder === 'asc' ? cmp : -cmp;
        });

        return sorted;
    };

    const filteredAndSortedLessons = React.useMemo(
        () => applySorting(applyFilters(lessons && lessons.length ? lessons : [])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lessons, filters, sortColumn, sortOrder],
    );

    const applyGrouping = items => {
        if (!items || !groupByColumn) return null;

        const groups = {};

        items.forEach(item => {
            let key = '';
            switch (groupByColumn) {
                case 'tag':
                    key = item.tag || 'Unspecified';
                    break;
                case 'audience':
                    key = getAudienceInfo(item.audience).label || 'Unspecified';
                    break;
                default:
                    key = 'Other';
            }
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(item);
        });

        let entries = Object.entries(groups).map(([groupName, groupItems]) => ({
            groupName,
            items: groupItems,
        }));

        if (groupBySort === 'asc') {
            entries.sort((a, b) => a.groupName.localeCompare(b.groupName));
        } else if (groupBySort === 'desc') {
            entries.sort((a, b) => b.groupName.localeCompare(a.groupName));
        }

        return entries;
    };

    // Visible IDs for select-all-on-hover header behavior (like RAID)
    const visibleIds = React.useMemo(() => (filteredAndSortedLessons || []).map(item => item.id), [filteredAndSortedLessons]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRows.includes(id));

    const handleToggleSelectAllVisible = () => {
        if (allVisibleSelected) {
            // Unselect all visible
            setSelectedRows(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            // Merge visible into selection
            setSelectedRows(prev => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const renderIdCell = (row, index) => {
        const showCheckbox = isRowSelected(row.id) || hoveredRowId === row.id;
        if (showCheckbox) {
            return (
                <input
                    type="checkbox"
                    className={styles.checkboxTable}
                    checked={isRowSelected(row.id)}
                    onChange={e => {
                        e.stopPropagation();
                        toggleRowSelected(row.id);
                    }}
                />
            );
        }
        return String(index + 1).padStart(4, '0');
    };

    const renderBodyCell = (row, col, index) => {
        const isEditingIssue = editingLessonId === row.id && editingFieldName === 'issue';
        const isEditingAction = editingLessonId === row.id && editingFieldName === 'action';
        const isEditingRecommendation = editingLessonId === row.id && editingFieldName === 'recomendation';

        switch (col) {
            case 'issue':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.issue }}>
                        {isEditingIssue ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingFieldValues.issue}
                                onChange={e => {
                                    e.stopPropagation();
                                    setEditingFieldValues(prev => ({ ...prev, issue: e.target.value }));
                                }}
                                onBlur={e => {
                                    e.stopPropagation();
                                    saveEditing(row);
                                }}
                                onKeyDown={e => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') saveEditing(row);
                                    if (e.key === 'Escape') cancelEditing();
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div className="d-flex align-items-center">
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <div
                                        className={styles.taskText}
                                        title={row.issue}
                                        onClick={e => startEditing(e, row, 'issue')}
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: '100%',
                                        }}>
                                        {row.issue}
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div className={styles.commentIcon} onClick={() => handleOpenLessonSidebar(row)} style={{ cursor: 'pointer' }}>
                                        {row?.comments_count > 0 ? <Comment16Filled color="var(--Brand-Secondary)" /> : <CommentAdd16Regular />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </td>
                );
            case 'action':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.action }}>
                        {isEditingAction ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingFieldValues.action}
                                onChange={e => {
                                    e.stopPropagation();
                                    setEditingFieldValues(prev => ({ ...prev, action: e.target.value }));
                                }}
                                onBlur={e => {
                                    e.stopPropagation();
                                    saveEditing(row);
                                }}
                                onKeyDown={e => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') saveEditing(row);
                                    if (e.key === 'Escape') cancelEditing();
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div
                                className={styles.taskText}
                                title={row.action || '—'}
                                onClick={e => startEditing(e, row, 'action')}
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    color: row.action ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)',
                                }}>
                                {row.action || 'Pending Input'}
                            </div>
                        )}
                    </td>
                );
            case 'recomendation':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.recomendation }}>
                        {isEditingRecommendation ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingFieldValues.recomendation}
                                onChange={e => {
                                    e.stopPropagation();
                                    setEditingFieldValues(prev => ({ ...prev, recomendation: e.target.value }));
                                }}
                                onBlur={e => {
                                    e.stopPropagation();
                                    saveEditing(row);
                                }}
                                onKeyDown={e => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') saveEditing(row);
                                    if (e.key === 'Escape') cancelEditing();
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div
                                className={styles.taskText}
                                title={row.recomendation || '—'}
                                onClick={e => startEditing(e, row, 'recomendation')}
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    color: row.recomendation ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)',
                                }}>
                                {row.recomendation || 'Pending Input'}
                            </div>
                        )}
                    </td>
                );
            case 'tag':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.tag }}>
                        <LessonTagManagementMenu
                            lessonData={lessons}
                            onTagSelect={tag => {
                                dispatch(
                                    PatchLesson({
                                        id: row.id,
                                        body: { tag: tag.name },
                                    }),
                                );
                            }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: '32px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <span className={styles.tableRegularText} style={{ color: row.tag ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)' }}>
                                    {row.tag || 'Unspecified'}
                                </span>
                            </div>
                        </LessonTagManagementMenu>
                    </td>
                );
            case 'audience':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.audience }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getAudienceInfo(row?.audience).color,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getAudienceInfo(row?.audience).label}
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '160px', minWidth: '160px' }}>
                                <MenuList>
                                    {AUDIENCE_OPTIONS.map(option => (
                                        <MenuItem
                                            key={option.value}
                                            className={styles.statusMenuItem}
                                            onClick={() =>
                                                dispatch(
                                                    PatchLesson({
                                                        id: row.id,
                                                        body: { audience: getAudienceValueForBackend(option.value) },
                                                    }),
                                                )
                                            }
                                            style={{ backgroundColor: option.color, color: 'white' }}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'loggedOn':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.loggedOn }}>
                        <div className="d-flex align-items-center gap-2 px-2 justify-content-start">
                            <div>
                                <CustomAvatar name={row.created_by_name || ''} size={20} />
                            </div>
                            <span style={{ width: 80 }} className={styles.tableRegularText}>
                                {formatDate(row.created)}
                            </span>
                        </div>
                    </td>
                );
            case 'updatedOn':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.updatedOn }}>
                        <div className="d-flex align-items-center gap-2 px-2 justify-content-start">
                            <div>
                                <CustomAvatar name={row.last_updated_by_name || ''} size={20} />
                            </div>
                            <span style={{ width: 80 }} className={styles.tableRegularText}>
                                {formatLastUpdated(row.last_updated)}
                            </span>
                        </div>
                    </td>
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.viewHeight}>
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-4">
                    <h4 className={styles.pageTitle + ' mb-0'}>Lessons Learnt</h4>
                </div>
                <div className="d-flex gap-4">
                    {selectedRows.length > 0 ? (
                        <div style={{ marginBottom: 8 }} className="d-flex align-items-center gap-2">
                            {/* Lens AI Analysis */}
                            <button
                                type="button"
                                className={`${styles.bulkUpdateMenu} d-flex align-items-center`}
                                onClick={() => {
                                    setSelectedSummaryTab('key-themes');
                                    setShowLensAIAnalysis(true);
                                }}>
                                <Wand16Regular className="me-1" /> Lens AI Analysis
                            </button>
                            {/* Bulk Audience update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Audience <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        {AUDIENCE_OPTIONS.map(option => (
                                            <MenuItem
                                                key={option.value}
                                                onClick={() =>
                                                    dispatch(
                                                        BulkUpdateLessons({
                                                            lessonIds: selectedRows,
                                                            updates: { audience: getAudienceValueForBackend(option.value) },
                                                        }),
                                                    )
                                                }>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>

                            {/* Bulk Tag update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Tag <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '220px', minWidth: '220px' }}>
                                    <MenuList>
                                        <div className="p-1">
                                            {Array.from(
                                                new Set([
                                                    // Tags defined at project level
                                                    ...(lessonTags || []).map(tag => tag.name).filter(name => name && name.trim() !== ''),
                                                    // Tags already used on lessons
                                                    ...(lessons || []).map(item => item.tag).filter(tag => tag && tag.trim() !== ''),
                                                ]),
                                            ).map(tagName => (
                                                <MenuItem
                                                    key={tagName}
                                                    onClick={() =>
                                                        dispatch(
                                                            BulkUpdateLessons({
                                                                lessonIds: selectedRows,
                                                                updates: { tag: tagName },
                                                            }),
                                                        )
                                                    }>
                                                    {tagName}
                                                </MenuItem>
                                            ))}
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>

                            {/* Bulk export */}
                            <span
                                className={styles.linkText}
                                onClick={handleExportSelectedLessonsCSV}
                                style={{ cursor: exportCSVLoading ? 'not-allowed' : 'pointer', opacity: exportCSVLoading ? 0.6 : 1 }}>
                                Export ({selectedRows.length})
                            </span>
                            {/* Bulk delete */}
                            <span
                                role="button"
                                className={styles.linkText}
                                onClick={() => {
                                    deleteModalRef?.current?.click();
                                }}>
                                Delete ({selectedRows.length})
                            </span>
                        </div>
                    ) : (
                        <>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <Filter20Regular className="me-1" />
                                        Filter ({filters.filter(f => f.column && f.value).length})
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Filter Lessons By</h6>
                                                {filters.some(filter => filter.column && filter.value) && (
                                                    <span className={styles.secondaryBtnText} onClick={handleClearAllFilters}>
                                                        Clear All
                                                    </span>
                                                )}
                                            </div>

                                            {filters.map(filter => (
                                                <div key={filter.id} className="d-flex align-items-center gap-2 mb-2">
                                                    <select
                                                        className={'form-select form-select-sm ' + styles.filterSelect}
                                                        value={filter.column}
                                                        onChange={e => handleUpdateFilter(filter.id, 'column', e.target.value)}>
                                                        <option value="">Select Column</option>
                                                        {getFilterColumnOptions().map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <select
                                                        className={'form-select form-select-sm ' + styles.filterSelect}
                                                        value={filter.value}
                                                        onChange={e => handleUpdateFilter(filter.id, 'value', e.target.value)}
                                                        disabled={!filter.column}>
                                                        <option value="">Filter</option>
                                                        {getFilterValueOptions(filter.column).map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    {filters.length > 1 && (
                                                        <button className="btn btn-link p-0" onClick={() => handleRemoveFilter(filter.id)} style={{ color: '#6c757d' }}>
                                                            ✕
                                                        </button>
                                                    )}
                                                </div>
                                            ))}

                                            <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleAddFilter}>
                                                Add another filter
                                            </span>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>

                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <ArrowSort20Regular className="me-1" />
                                        Sort
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Sort Lessons By</h6>
                                                {(sortColumn || sortOrder) && (
                                                    <span className={styles.secondaryBtnText} onClick={handleClearSort}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center gap-2">
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={sortColumn}
                                                    onChange={e => setSortColumn(e.target.value)}>
                                                    <option value="">Select Column</option>
                                                    {getSortColumnOptions().map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={sortOrder}
                                                    onChange={e => setSortOrder(e.target.value)}
                                                    disabled={!sortColumn}>
                                                    <option value="">Order</option>
                                                    {getSortOrderOptions().map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <Grid20Regular className="me-1" />
                                        Group by
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Group Lessons By</h6>
                                                {(groupByColumn || groupBySort) && (
                                                    <span
                                                        className={styles.secondaryBtnText}
                                                        onClick={() => {
                                                            setGroupByColumn('');
                                                            setGroupBySort('');
                                                            setCollapsedGroups(new Set());
                                                        }}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center gap-2">
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={groupByColumn}
                                                    onChange={e => setGroupByColumn(e.target.value)}>
                                                    <option value="">Select Column</option>
                                                    <option value="tag">Tag</option>
                                                    <option value="audience">Audience</option>
                                                </select>

                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={groupBySort}
                                                    onChange={e => setGroupBySort(e.target.value)}
                                                    disabled={!groupByColumn}>
                                                    <option value="">Sorting</option>
                                                    <option value="asc">Ascending</option>
                                                    <option value="desc">Descending</option>
                                                </select>
                                            </div>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <EyeOff20Regular className="me-1" />
                                        Hide
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '200px', minWidth: '200px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <h6 className={styles.tableFilterHeader + ' mb-3'}>Display Columns</h6>

                                            {getColumnVisibilityOptions().map(option => (
                                                <div key={option.value} className="d-flex align-items-center mb-2">
                                                    <input
                                                        type="checkbox"
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            marginRight: '8px',
                                                            cursor: 'pointer',
                                                        }}
                                                        checked={option.value === 'showAll' ? showAllColumns : isColumnVisible(option.value)}
                                                        onChange={e => handleColumnVisibility(option.value, e.target.checked)}
                                                    />
                                                    <span className={styles.tableRegularText}>{option.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <div className="d-flex align-items-center">
                                <button className={styles.regularSecondaryBtn} onClick={handleAddLessonClick}>
                                    Add Lesson
                                </button>
                                <Menu>
                                    <MenuTrigger>
                                        <button className={styles.menuButton}>
                                            <ChevronDown12Regular />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem
                                                icon={<ArrowExport20Regular />}
                                                style={{ color: 'var(--Brand-Secondary)' }}
                                                disabled={exportCSVLoading || !lessons?.length}
                                                onClick={handleExportLessonsCSV}>
                                                Export Lessons as CSV
                                            </MenuItem>
                                            <MenuDivider />
                                            <MenuItem
                                                icon={<Table20Regular />}
                                                style={{ color: 'var(--Brand-Secondary)' }}
                                                disabled={uploadCSVBtnLoading}
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                                Import Lessons from CSV
                                            </MenuItem>
                                            <MenuDivider />
                                            <MenuItem
                                                icon={<ArrowDownload20Regular />}
                                                style={{ color: 'var(--Brand-Secondary)' }}
                                                disabled={downloadTemplateLoading}
                                                onClick={() => dispatch(DownloadLessonsTemplate())}>
                                                Download Lessons Template
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Hidden file input for Lessons CSV upload */}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleLessonsFileUpload} className="d-none" ref={fileInputRef} disabled={uploadCSVBtnLoading} />

            {/* Table */}
            <div className={styles.tableContainer} ref={tableContainerRef}>
                <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={event => {
                        const { active, over } = event;
                        if (!over || active.id === over.id) return;
                        const oldIndex = columnOrder.indexOf(String(active.id));
                        const newIndex = columnOrder.indexOf(String(over.id));
                        if (oldIndex === -1 || newIndex === -1) return;
                        const updated = [...columnOrder];
                        updated.splice(oldIndex, 1);
                        updated.splice(newIndex, 0, String(active.id));
                        setColumnOrder(updated);
                    }}>
                    <table style={{ minWidth: 900 }}>
                        <colgroup>
                            <col style={{ width: 60 }} />
                            {columnOrder.filter(isColumnVisible).map(col => (
                                <col key={col} style={{ width: columnWidths[col] }} />
                            ))}
                            <col style={{ width: 30 }} />
                        </colgroup>
                        <thead>
                            <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                                {!groupByColumn && (
                                    <tr className={styles.tableHeader}>
                                        <th
                                            className={styles.headerRowItem}
                                            style={{ width: 60, minWidth: 60, maxWidth: 60 }}
                                            onMouseEnter={() => setHeaderIdHovered(true)}
                                            onMouseLeave={() => setHeaderIdHovered(false)}>
                                            {headerIdHovered || allVisibleSelected ? (
                                                <input type="checkbox" className={styles.checkboxTable} checked={allVisibleSelected} onChange={handleToggleSelectAllVisible} />
                                            ) : (
                                                <span className={styles.headerRowItem}>ID</span>
                                            )}
                                        </th>

                                        {columnOrder.filter(isColumnVisible).map(col => {
                                            const labelMap = {
                                                issue: 'Issue / Observation',
                                                action: 'Action Taken (This Project)',
                                                recomendation: 'Recommendation (Future)',
                                                tag: 'Tag',
                                                audience: 'Audience',
                                                loggedOn: 'Logged on',
                                                updatedOn: 'Updated on',
                                            };

                                            // Make the main text columns resizable like RAID
                                            if (col === 'issue' || col === 'action' || col === 'recomendation') {
                                                const minWidth = 200;
                                                const maxWidth = 800;
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths[col]}
                                                            minWidth={minWidth}
                                                            maxWidth={maxWidth}
                                                            onResize={newWidth => handleColumnResize(col, newWidth)}>
                                                            {labelMap[col]}
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }

                                            // Other columns stay draggable but fixed-width
                                            return (
                                                <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                    <div>{labelMap[col]}</div>
                                                </SortableHeaderCell>
                                            );
                                        })}

                                        <th className={styles.headerRowItem} style={{ width: 30, minWidth: 30, maxWidth: 30 }}></th>
                                    </tr>
                                )}
                            </SortableContext>
                        </thead>
                        <tbody>
                            {(() => {
                                const grouped = applyGrouping(filteredAndSortedLessons);
                                if (!grouped) {
                                    return filteredAndSortedLessons?.map((row, index) => (
                                        <tr
                                            key={row.id || index}
                                            className={styles.tableRow}
                                            onMouseEnter={e => {
                                                setHoveredRowId(row.id);
                                                const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                                if (menuIcon) {
                                                    menuIcon.style.display = 'flex';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                setHoveredRowId(null);
                                                const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                                if (menuIcon) {
                                                    menuIcon.style.display = 'none';
                                                }
                                            }}>
                                            <td className={styles.tableRowItem}>{renderIdCell(row, index)}</td>
                                            {columnOrder.filter(isColumnVisible).map(col => renderBodyCell(row, col, index))}
                                            <td className={styles.tableRowItem}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                    <div className="menu-hover-icon" style={{ display: 'none' }}>
                                                        <Menu>
                                                            <MenuTrigger>
                                                                <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <MoreVertical16Regular style={{ width: '16px', height: '16px' }} />
                                                                </div>
                                                            </MenuTrigger>
                                                            <MenuPopover>
                                                                <MenuList>
                                                                    <MenuItem onClick={() => handleDeleteRow(row)}>Delete</MenuItem>
                                                                </MenuList>
                                                            </MenuPopover>
                                                        </Menu>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ));
                                }

                                const visibleColumnsCount = 1 + columnOrder.filter(isColumnVisible).length + 1;

                                return grouped.map(group => (
                                    <React.Fragment key={group.groupName}>
                                        {/* Group header row */}
                                        <tr>
                                            <td colSpan={visibleColumnsCount} style={{ padding: '8px 12px' }}>
                                                <span
                                                    className={styles.todoText}
                                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                    onClick={() =>
                                                        setCollapsedGroups(prev => {
                                                            const next = new Set(prev);
                                                            if (next.has(group.groupName)) next.delete(group.groupName);
                                                            else next.add(group.groupName);
                                                            return next;
                                                        })
                                                    }>
                                                    <ChevronDown20Regular className="me-1" style={collapsedGroups.has(group.groupName) ? { transform: 'rotate(-90deg)' } : {}} />
                                                    {group.groupName} ({group.items.length})
                                                </span>
                                            </td>
                                        </tr>

                                        {/* Per-group column header row */}
                                        {!collapsedGroups.has(group.groupName) && (
                                            <tr className={styles.tableHeader}>
                                                <th className={styles.headerRowItem} style={{ width: 60, minWidth: 60, maxWidth: 60 }}>
                                                    ID
                                                </th>
                                                {columnOrder.filter(isColumnVisible).map(col => {
                                                    const labelMap = {
                                                        issue: 'Issue / Observation',
                                                        action: 'Action Taken (This Project)',
                                                        recomendation: 'Recommendation (Future)',
                                                        tag: 'Tag',
                                                        audience: 'Audience',
                                                        loggedOn: 'Logged on',
                                                        updatedOn: 'Updated on',
                                                    };
                                                    return (
                                                        <th key={col} className={styles.headerRowItem} style={{ width: columnWidths[col] }}>
                                                            {labelMap[col]}
                                                        </th>
                                                    );
                                                })}
                                                <th className={styles.headerRowItem} style={{ width: 30, minWidth: 30, maxWidth: 30 }} />
                                            </tr>
                                        )}

                                        {/* Group rows */}
                                        {!collapsedGroups.has(group.groupName) &&
                                            group.items.map((row, index) => (
                                                <tr
                                                    key={row.id || index}
                                                    className={styles.tableRow}
                                                    onMouseEnter={e => {
                                                        setHoveredRowId(row.id);
                                                        const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                                        if (menuIcon) {
                                                            menuIcon.style.display = 'flex';
                                                        }
                                                    }}
                                                    onMouseLeave={e => {
                                                        setHoveredRowId(null);
                                                        const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                                        if (menuIcon) {
                                                            menuIcon.style.display = 'none';
                                                        }
                                                    }}>
                                                    <td className={styles.tableRowItem}>{renderIdCell(row, index)}</td>
                                                    {columnOrder.filter(isColumnVisible).map(col => renderBodyCell(row, col, index))}
                                                    <td className={styles.tableRowItem}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                            <div className="menu-hover-icon" style={{ display: 'none' }}>
                                                                <Menu>
                                                                    <MenuTrigger>
                                                                        <div
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                            }}>
                                                                            <MoreVertical16Regular style={{ width: '16px', height: '16px' }} />
                                                                        </div>
                                                                    </MenuTrigger>
                                                                    <MenuPopover>
                                                                        <MenuList>
                                                                            <MenuItem onClick={() => handleDeleteRow(row)}>Delete</MenuItem>
                                                                        </MenuList>
                                                                    </MenuPopover>
                                                                </Menu>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                ));
                            })()}

                            {/* Add Lesson row */}
                            {(() => {
                                // Calculate total number of columns: ID + visible columns + actions
                                const visibleColumnsCount = columnOrder.filter(col => isColumnVisible(col)).length;
                                const totalColumns = 1 + visibleColumnsCount + 1; // ID + visible columns + actions

                                return (
                                    <tr
                                        ref={addRowRef}
                                        className={`${styles.tableRow} ${highlightAddRow ? styles.highlightAddRow : ''}`}
                                        style={{ backgroundColor: '#F3FDFE', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                        {/* Single cell with colspan spanning all columns */}
                                        <td className={styles.tableRowItem} colSpan={totalColumns}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="flex-fill">
                                                    <input
                                                        ref={newIssueInputRef}
                                                        type="text"
                                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                                        value={newIssue}
                                                        style={{ border: '1px solid #dddee4', marginLeft: '60px', maxWidth: '400px' }}
                                                        onChange={e => setNewIssue(e.target.value)}
                                                        onKeyDown={async e => {
                                                            if (e.key === 'Enter') {
                                                                await handleCreateLesson();
                                                            } else if (e.key === 'Escape') {
                                                                setNewIssue('');
                                                            }
                                                        }}
                                                        placeholder="Add New Issue / Observation"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })()}
                        </tbody>
                    </table>
                </DndContext>
            </div>

            {/* Delete confirmation */}
            <DoubleConfirmationModal
                message="Deleting a Lesson will permanently remove it from the Project."
                handleAction={() => {
                    if (selectedRows.length > 0) {
                        dispatch(BulkDeleteLessons({ lessonIds: selectedRows }));
                        setSelectedRows([]);
                    } else if (selectedRowForDelete) {
                        dispatch(DeleteLesson(selectedRowForDelete.id));
                        setSelectedRowForDelete(null);
                    }
                }}>
                <span ref={deleteModalRef} style={{ display: 'none' }}></span>
            </DoubleConfirmationModal>
            <LessonDetailsSidebar open={lessonSidebarOpen} onClose={handleCloseLessonSidebar} lesson={selectedLessonForSidebar} />

            {/* Lens AI Document Analysis Sidebar */}
            <OverlayDrawer
                style={{ minWidth: 872 }}
                type="overlay"
                position="end"
                open={showLensAIAnalysis}
                onOpenChange={(_, { open }) => {
                    setShowLensAIAnalysis(open);
                    if (!open) setSelectedSummaryTab('key-themes');
                }}>
                <DrawerBody className="position-relative" style={{ padding: 0 }}>
                    <div className="d-flex align-items-center justify-content-between px-3 mt-3">
                        <div className="d-flex align-items-center">
                            <Wand20Regular color="var(--Brand-Secondary)" className="me-1" />
                            <span style={{ fontWeight: 600, fontSize: 16 }}>Lens AI Analysis</span>
                        </div>
                        <Dismiss20Regular onClick={() => setShowLensAIAnalysis(false)} role="button" color="var(--Neutral-Gray-6)" style={{ cursor: 'pointer' }} />
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 px-3 mt-3" style={{ borderBottom: '1px solid var(--Neutral-Gray-4)', paddingBottom: 12 }}>
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                            <label htmlFor="lessons-topic-select" className="mb-0" style={{ fontSize: 12, color: 'var(--Brand-Primary)', whiteSpace: 'nowrap' }}>
                                Select topic to view:
                            </label>
                            <select
                                id="lessons-topic-select"
                                value={selectedSummaryTab}
                                onChange={e => setSelectedSummaryTab(e.target.value)}
                                className="form-select form-select-sm"
                                style={{ maxWidth: 320, fontSize: 12 }}>
                                {LESSONS_AI_SUMMARY_SECTIONS.map(({ id, label }) => (
                                    <option key={id} value={id}>
                                        {label}
                                    </option>
                                ))}
                                <option value={LESSONS_AI_SHOW_ALL}>Show all</option>
                            </select>
                        </div>
                        <div
                            onClick={() => {
                                if (!lessonsSummaryLoading) dispatch(GetLessonsAISummary({ lessonIds: selectedRows, regenerate: true }));
                            }}
                            style={{
                                color: lessonsSummaryLoading ? 'var(--Neutral-Gray-6)' : 'var(--Brand-Secondary)',
                                cursor: lessonsSummaryLoading ? 'not-allowed' : 'pointer',
                                opacity: lessonsSummaryLoading ? 0.5 : 1,
                                marginLeft: 'auto',
                            }}
                            className="d-flex align-items-center"
                            role="button">
                            {lessonsSummaryLoading ? <Wand20Regular className="me-1" style={{ animation: 'pulse 1s ease-in-out infinite' }} /> : <ArrowClockwise16Regular className="me-1" />}
                            <span>{lessonsSummaryLoading ? 'Generating...' : 'Regenerate'}</span>
                        </div>
                    </div>
                    <div className="px-3 mt-4 position-relative" style={{ minHeight: 200 }}>
                        {lessonsSummaryLoading && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <Wand20Regular style={{ color: 'var(--Brand-Secondary)' }} />
                                <p className="mt-2 mb-0" style={{ color: 'var(--Brand-Primary)', fontSize: 14 }}>Loading results</p>
                            </div>
                        )}
                        {lessonsSummaryError && (
                            <div className="alert alert-danger" role="alert">
                                <strong>Error:</strong> {lessonsSummaryError}
                                <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => dispatch(GetLessonsAISummary({ lessonIds: selectedRows, regenerate: true }))}>
                                    Retry
                                </button>
                            </div>
                        )}
                        {!lessonsSummaryLoading && !lessonsSummaryError && lessonsSummaryData?.sections && (
                            <div style={{ color: 'var(--Brand-Primary)', fontSize: 12, lineHeight: 1.6 }}>
                                {selectedSummaryTab === LESSONS_AI_SHOW_ALL
                                    ? LESSONS_AI_SUMMARY_SECTIONS.map((section, index) => (
                                          <section
                                              key={section.id}
                                              style={{
                                                  marginBottom: index < LESSONS_AI_SUMMARY_SECTIONS.length - 1 ? 20 : 0,
                                                  paddingBottom: index < LESSONS_AI_SUMMARY_SECTIONS.length - 1 ? 20 : 0,
                                                  borderBottom:
                                                      index < LESSONS_AI_SUMMARY_SECTIONS.length - 1 ? '1px solid var(--Neutral-Gray-4)' : 'none',
                                              }}>
                                              <h3
                                                  className="mb-2 mt-0"
                                                  style={{
                                                      fontSize: 14,
                                                      fontWeight: 600,
                                                      color: 'var(--Brand-Secondary)',
                                                      letterSpacing: '0.01em',
                                                  }}>
                                                  {section.label}
                                              </h3>
                                              <div dangerouslySetInnerHTML={{ __html: lessonsSummaryData.sections[section.key] || '<p>No content.</p>' }} />
                                          </section>
                                      ))
                                    : LESSONS_AI_SUMMARY_SECTIONS.filter(t => t.id === selectedSummaryTab).map(t => (
                                          <div key={t.id} dangerouslySetInnerHTML={{ __html: lessonsSummaryData.sections[t.key] || '<p>No content.</p>' }} />
                                      ))}
                            </div>
                        )}
                        {!lessonsSummaryLoading && !lessonsSummaryError && !lessonsSummaryData?.sections && (
                            <div className="text-muted" style={{ fontSize: 14 }}>Click &quot;Regenerate&quot; to analyze the selected lessons.</div>
                        )}
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </div>
    );
};

export default Lessons;
